/** @file Grid layer: square or iso lattice lines over the viewport, thinned to at least 8 px apart. */
import type { Camera } from '@pose2d/geometry/types';
import { affine } from '@pose2d/math/affine';
import type { Sink } from '../draw/createSink';
import { emitLine } from '../draw/emitLine';
import type { GridSpec } from '../overlayOptions';
import type { OverlayInput } from '../overlayInput';
import { OVERLAY_STYLE } from '../overlayStyle';
import { pixelsPerUnit } from './pixelsPerUnit';
import { gridLattice } from './gridLattice';
import { visibleWorldBox } from './visibleWorldBox';

const p: [number, number] = [0, 0];
const line = new Float64Array(4);
const worldBox: [number, number, number, number] = [0, 0, 0, 0];
const span: [number, number, number, number] = [0, 0, 0, 0];

/** Pixel position of lattice point (u, v), written into line[at], line[at + 1]. */
function toPixel(camera: Camera, grid: GridSpec, u: number, v: number, at: number): void {
  gridLattice.toWorld(grid, u, v, p);
  affine.apply(camera.worldToPixel, p[0], p[1], p);
  line[at] = p[0];
  line[at + 1] = p[1];
}

/** Smallest power-of-two step (in cells) whose lines are at least `OVERLAY_STYLE.minGridPx` apart. */
function cellStep(spacingPx: number): number {
  let step = 1;
  while (spacingPx * step < OVERLAY_STYLE.minGridPx && step < 1 << 20) step *= 2;
  return step;
}

/** A lattice range [uMin, uMax, vMin, vMax] as [along lo, along hi, across lo, across hi] for `family` (scratch). */
function orient(range: [number, number, number, number], family: 0 | 1): [number, number, number, number] {
  span[0] = family === 0 ? range[0] : range[2];
  span[1] = family === 0 ? range[1] : range[3];
  span[2] = family === 0 ? range[2] : range[0];
  span[3] = family === 0 ? range[3] : range[1];
  return span;
}

/**
 * Lines of one family: constant u (family 0) or constant v (family 1) across the other coordinate's range, at the
 * multiples of the step in view. They are counted by index, not by `k += step` (which stops advancing once k is past
 * 2^53 step, e.g. a camera 1e17 units out), and a multiple that rounds onto the previous one is drawn once.
 */
function drawFamily(sink: Sink, input: OverlayInput, grid: GridSpec, family: 0 | 1): void {
  const [lo, hi, oLo, oHi] = orient(gridLattice.range(grid, visibleWorldBox(sink, worldBox)), family);
  const step = cellStep(gridLattice.spacing(grid)[family] * pixelsPerUnit(input.camera));
  const first = Math.ceil(lo / step), count = Math.min(OVERLAY_STYLE.maxGridLines, Math.floor(hi / step) - first + 1);
  for (let n = 0, prev = NaN; n < count; n++) {
    const k = (first + n) * step;
    if (k === prev) continue;
    prev = k;
    toPixel(input.camera, grid, family === 0 ? k : oLo, family === 0 ? oLo : k, 0);
    toPixel(input.camera, grid, family === 0 ? k : oHi, family === 0 ? oHi : k, 2);
    emitLine(sink, line, 1, OVERLAY_STYLE.grid);
  }
}

/** `options.grid`: square or iso tile lines (the footprint is drawFootprint). */
export function drawGrid(sink: Sink, input: OverlayInput): void {
  const grid = input.options.grid;
  if (grid === undefined || !(grid.tileW > 0) || !(grid.tileH > 0)) return;
  drawFamily(sink, input, grid, 0);
  drawFamily(sink, input, grid, 1);
}
